import React, { useState } from 'react';
import { ArrowLeftIcon, PlusIcon, MailIcon, FileTextIcon, InfoIcon, ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon } from 'lucide-react';
import { BrokerInviteModal } from './BrokerInviteModal';
import { BrokerRatesModal } from './BrokerRatesModal';
import { ContractAgreementModal } from './ContractAgreementModal';
interface TruckRate {
  truckType: string;
  rate: number;
}
interface Broker {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'active' | 'pending' | 'inactive';
  defaultRate: number;
  rates: {
    [key: string]: number;
  };
  inviteSent?: boolean;
  contractSigned?: boolean;
}
interface ManageBrokersPageProps {
  onBack: () => void;
}
export const ManageBrokersPage = ({
  onBack
}: ManageBrokersPageProps) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null);
  // Updated truck types
  const truckTypes = ['Dump Truck 10', 'Dump Truck 12', 'Dump Truck 14+', 'Strong Arm 12', 'Strong Arm 14', 'Strong Arm 16+', 'Side Dump', 'Super Side 30tn+', 'Jumbo Side 35tn+', 'Belly Dump', 'Double Belly'];
  // Sample brokers data
  const [brokers, setBrokers] = useState<Broker[]>([{
    id: 'broker1',
    name: 'John Smith',
    company: 'Alpine Hauling',
    email: 'john@alpinehauling.com',
    phone: '555-123-4567',
    status: 'active',
    defaultRate: 100.0,
    rates: {
      'Dump Truck 10': 95.0,
      'Dump Truck 12': 100.0,
      'Dump Truck 14+': 105.0,
      'Strong Arm 12': 110.0,
      'Strong Arm 14': 115.0,
      'Strong Arm 16+': 120.0,
      'Side Dump': 125.0,
      'Super Side 30tn+': 130.0,
      'Jumbo Side 35tn+': 135.0,
      'Belly Dump': 140.0,
      'Double Belly': 145.0
    },
    contractSigned: true
  }, {
    id: 'broker2',
    name: 'Sarah Johnson',
    company: 'Blue Ridge Transport',
    email: 'sarah@blueridgetransport.com',
    phone: '555-987-6543',
    status: 'active',
    defaultRate: 95.0,
    rates: {
      'Dump Truck 10': 90.0,
      'Dump Truck 12': 95.0,
      'Dump Truck 14+': 100.0,
      'Strong Arm 12': 105.0,
      'Strong Arm 14': 110.0,
      'Strong Arm 16+': 115.0,
      'Side Dump': 120.0,
      'Super Side 30tn+': 125.0,
      'Jumbo Side 35tn+': 130.0,
      'Belly Dump': 135.0,
      'Double Belly': 140.0
    },
    contractSigned: true
  }, {
    id: 'broker3',
    name: 'Mike Davis',
    company: 'Dump Truck Brokers Inc.',
    email: 'mike@dumptruckbrokers.com',
    phone: '555-456-7890',
    status: 'active',
    defaultRate: 110.0,
    rates: {
      'Dump Truck 10': 100.0,
      'Dump Truck 12': 105.0,
      'Dump Truck 14+': 110.0,
      'Strong Arm 12': 115.0,
      'Strong Arm 14': 120.0,
      'Strong Arm 16+': 125.0,
      'Side Dump': 130.0,
      'Super Side 30tn+': 135.0,
      'Jumbo Side 35tn+': 140.0,
      'Belly Dump': 145.0,
      'Double Belly': 150.0
    },
    contractSigned: false
  }, {
    id: 'broker4',
    name: 'Lisa Chen',
    company: 'Urban Logistics',
    email: 'lisa@urbanlogistics.com',
    phone: '555-789-0123',
    status: 'pending',
    defaultRate: 105.0,
    rates: {
      'Dump Truck 10': 95.0,
      'Dump Truck 12': 100.0,
      'Dump Truck 14+': 105.0,
      'Strong Arm 12': 110.0,
      'Strong Arm 14': 115.0,
      'Strong Arm 16+': 120.0,
      'Side Dump': 125.0,
      'Super Side 30tn+': 130.0,
      'Jumbo Side 35tn+': 135.0,
      'Belly Dump': 140.0,
      'Double Belly': 145.0
    },
    inviteSent: true
  }, {
    id: 'dirt-logics-broker',
    name: 'Dirt Logics Broker',
    company: 'Dirt Logics Brokerage',
    email: 'broker@dirtlogics.com',
    phone: '555-321-7654',
    status: 'active',
    defaultRate: 98.0,
    rates: {
      'Dump Truck 10': 93.0,
      'Dump Truck 12': 98.0,
      'Dump Truck 14+': 103.0,
      'Strong Arm 12': 108.0,
      'Strong Arm 14': 113.0,
      'Strong Arm 16+': 118.0,
      'Side Dump': 123.0,
      'Super Side 30tn+': 128.0,
      'Jumbo Side 35tn+': 133.0,
      'Belly Dump': 138.0,
      'Double Belly': 143.0
    },
    contractSigned: true
  }]);
  const handleAddBroker = (newBroker: Omit<Broker, 'id' | 'rates'>) => {
    const id = `broker${brokers.length + 1}`;
    // Create default rates for all truck types
    const rates: {
      [key: string]: number;
    } = {};
    truckTypes.forEach(type => {
      rates[type] = newBroker.defaultRate;
    });
    setBrokers([...brokers, {
      ...newBroker,
      id,
      rates,
      inviteSent: true
    }]);
    setShowInviteModal(false);
  };
  const handleUpdateRates = (brokerId: string, updatedRates: {
    [key: string]: number;
  }) => {
    setBrokers(brokers.map(broker => broker.id === brokerId ? {
      ...broker,
      rates: updatedRates
    } : broker));
    setShowRatesModal(false);
    setSelectedBroker(null);
  };
  const handleSendContract = (brokerId: string) => {
    setBrokers(brokers.map(broker => broker.id === brokerId ? {
      ...broker,
      contractSigned: false
    } : broker));
    setShowContractModal(false);
    setSelectedBroker(null);
  };
  const handleDeleteBroker = (brokerId: string) => {
    if (window.confirm('Are you sure you want to delete this broker?')) {
      setBrokers(brokers.filter(broker => broker.id !== brokerId));
    }
  };
  const handleEditRates = (broker: Broker) => {
    setSelectedBroker(broker);
    setShowRatesModal(true);
  };
  const handleSendAgreement = (broker: Broker) => {
    setSelectedBroker(broker);
    setShowContractModal(true);
  };
  return <div className="p-6 w-full" data-id="element-1939">
      <div className="flex justify-between items-center mb-6" data-id="element-1940">
        <div className="w-1/3" data-id="element-1941"></div>
        <h1 className="text-2xl font-bold text-center w-1/3" data-id="element-1942">Manage Brokers</h1>
        <div className="flex justify-end w-1/3" data-id="element-1943">
          <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={onBack} data-id="element-1944">
            Back to Dashboard
          </button>
        </div>
      </div>
      <div className="flex justify-between items-center mb-6" data-id="element-1945">
        <div className="w-1/3" data-id="element-1946"></div>
        <div className="w-1/3" data-id="element-1947"></div>
        <div className="flex justify-end w-1/3" data-id="element-1948">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={() => setShowInviteModal(true)} data-id="element-1949">
            <PlusIcon size={18} data-id="element-1950" />
            Add New Broker
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1951">
        <h2 className="text-lg font-bold mb-2 text-center" data-id="element-1952">
          Brokers and Rate Management
        </h2>
        <p className="text-gray-600 mb-4 text-center" data-id="element-1953">
          Set default rates for each broker and customize rates by truck type.
          You can also suppress rates for specific truck types.
        </p>
        <div className="overflow-x-auto" data-id="element-1954">
          <table className="w-full border-collapse" data-id="element-1955">
            <thead data-id="element-1956">
              <tr className="bg-gray-50 text-left" data-id="element-1957">
                <th className="p-3 border-b" data-id="element-1958">BROKER</th>
                <th className="p-3 border-b" data-id="element-1959">CONTACT</th>
                <th className="p-3 border-b" data-id="element-1960">DEFAULT RATE</th>
                <th className="p-3 border-b" data-id="element-1961">DUMP TRUCK 10</th>
                <th className="p-3 border-b" data-id="element-1962">DUMP TRUCK 12</th>
                <th className="p-3 border-b" data-id="element-1963">DUMP TRUCK 14+</th>
                <th className="p-3 border-b" data-id="element-1964">STRONG ARM 12</th>
                <th className="p-3 border-b" data-id="element-1965">STRONG ARM 14</th>
                <th className="p-3 border-b" data-id="element-1966">STRONG ARM 16+</th>
                <th className="p-3 border-b" data-id="element-1967">SIDE DUMP</th>
                <th className="p-3 border-b" data-id="element-1968">SUPER SIDE 30TN+</th>
                <th className="p-3 border-b" data-id="element-1969">JUMBO SIDE 35TN+</th>
                <th className="p-3 border-b" data-id="element-1970">BELLY DUMP</th>
                <th className="p-3 border-b" data-id="element-1971">DOUBLE BELLY</th>
                <th className="p-3 border-b" data-id="element-1972">ACTIONS</th>
              </tr>
            </thead>
            <tbody data-id="element-1973">
              {brokers.map(broker => <tr key={broker.id} className="border-b hover:bg-gray-50" data-id="element-1974">
                  <td className="p-3" data-id="element-1975">
                    <div className="font-medium" data-id="element-1976">{broker.company}</div>
                    {broker.status === 'pending' && <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full" data-id="element-1977">
                        Pending
                      </span>}
                  </td>
                  <td className="p-3" data-id="element-1978">
                    <div data-id="element-1979">{broker.name}</div>
                    <div className="text-sm text-gray-500" data-id="element-1980">{broker.email}</div>
                    <div className="text-sm text-gray-500" data-id="element-1981">{broker.phone}</div>
                  </td>
                  <td className="p-3" data-id="element-1982">${broker.defaultRate.toFixed(2)}</td>
                  {truckTypes.map(type => <td key={type} className="p-3" data-id="element-1983">
                      ${broker.rates[type]?.toFixed(2) || 'N/A'}
                    </td>)}
                  <td className="p-3" data-id="element-1984">
                    <div className="flex gap-2" data-id="element-1985">
                      <button className="text-blue-600 hover:text-blue-800" onClick={() => handleEditRates(broker)} data-id="element-1986">
                        Edit
                      </button>
                      {!broker.contractSigned && <button className="text-green-600 hover:text-green-800" onClick={() => handleSendAgreement(broker)} data-id="element-1987">
                          Contract
                        </button>}
                      <button className="text-red-600 hover:text-red-800" onClick={() => handleDeleteBroker(broker.id)} data-id="element-1988">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between mt-4" data-id="element-1989">
          <div className="flex items-center text-sm text-gray-500" data-id="element-1990">
            Showing {brokers.length} brokers
          </div>
          <div className="flex items-center gap-2" data-id="element-1991">
            <button className="p-1 border rounded" data-id="element-1992">
              <ChevronLeftIcon size={18} data-id="element-1993" />
            </button>
            <span className="text-sm" data-id="element-1994">Page 1 of 1</span>
            <button className="p-1 border rounded" data-id="element-1995">
              <ChevronRightIcon size={18} data-id="element-1996" />
            </button>
          </div>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6" data-id="element-1997">
        <div className="flex items-start gap-3" data-id="element-1998">
          <InfoIcon className="text-blue-500 mt-1 flex-shrink-0" size={20} data-id="element-1999" />
          <div data-id="element-2000">
            <h3 className="font-medium text-blue-800" data-id="element-2001">
              Managing Broker Relationships
            </h3>
            <p className="text-blue-700 mt-1" data-id="element-2002">
              Invite brokers to join your contractor network to streamline job
              assignments. Set custom rates for each truck type to simplify
              billing and payment processing. Send standardized agreements to
              ensure all parties understand terms and conditions.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-2003">
        <h2 className="text-lg font-bold mb-4 text-center" data-id="element-2004">
          Contract Templates
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2005">
          <div className="border rounded-md p-4" data-id="element-2006">
            <div className="flex justify-between items-start" data-id="element-2007">
              <div data-id="element-2008">
                <h3 className="font-medium" data-id="element-2009">Standard Broker Agreement</h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2010">
                  Basic terms for broker relationships
                </p>
              </div>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full" data-id="element-2011">
                Default
              </span>
            </div>
            <div className="flex gap-2 mt-4" data-id="element-2012">
              <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2013">
                Preview
              </button>
              <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2014">
                Edit
              </button>
            </div>
          </div>
          <div className="border rounded-md p-4" data-id="element-2015">
            <div data-id="element-2016">
              <h3 className="font-medium" data-id="element-2017">Long-term Partnership Agreement</h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2018">
                Extended terms for established brokers
              </p>
            </div>
            <div className="flex gap-2 mt-4" data-id="element-2019">
              <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2020">
                Preview
              </button>
              <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2021">
                Edit
              </button>
            </div>
          </div>
          <div className="border rounded-md p-4 border-dashed flex items-center justify-center" data-id="element-2022">
            <button className="text-blue-600 hover:text-blue-800 flex items-center gap-2" data-id="element-2023">
              <PlusIcon size={18} data-id="element-2024" />
              <span data-id="element-2025">Create New Template</span>
            </button>
          </div>
        </div>
      </div>
      {/* Broker Invite Modal */}
      {showInviteModal && <BrokerInviteModal onClose={() => setShowInviteModal(false)} onSave={handleAddBroker} truckTypes={truckTypes} data-id="element-2026" />}
      {/* Broker Rates Modal */}
      {showRatesModal && selectedBroker && <BrokerRatesModal broker={selectedBroker} truckTypes={truckTypes} onClose={() => {
      setShowRatesModal(false);
      setSelectedBroker(null);
    }} onSave={handleUpdateRates} data-id="element-2027" />}
      {/* Contract Agreement Modal */}
      {showContractModal && selectedBroker && <ContractAgreementModal broker={selectedBroker} onClose={() => {
      setShowContractModal(false);
      setSelectedBroker(null);
    }} onSend={handleSendContract} data-id="element-2028" />}
    </div>;
};